import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  Auth,
} from 'firebase/auth';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { authApi } from './api';

// Firebase config from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let auth: Auth | null = null;

function isFirebaseConfigured(): boolean {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId);
}

function getFirebaseMessaging(): Messaging | null {
  if (!isFirebaseConfigured()) return null;

  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  if (!messaging) {
    messaging = getMessaging(app);
  }
  return messaging;
}

/**
 * Request notification permission and register the push token with the backend.
 * Called after user logs in (authenticated mode only).
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!isFirebaseConfigured()) {
    console.warn('[push] Firebase not configured — skipping permission request');
    return false;
  }
  if (!('Notification' in window)) {
    console.warn('[push] Notification API not available');
    return false;
  }

  const permission = await Notification.requestPermission();
  console.log('[push] Permission result:', permission);
  if (permission !== 'granted') return false;

  try {
    const m = getFirebaseMessaging();
    if (!m) {
      console.warn('[push] Failed to get messaging instance');
      return false;
    }

    const swRegistration = await navigator.serviceWorker.getRegistration(
      '/firebase-cloud-messaging-push-scope',
    );
    console.log('[push] SW registration:', !!swRegistration, 'active:', !!swRegistration?.active);

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) console.warn('[push] VAPID key missing');
    const token = await getToken(m, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      await authApi.updatePushToken({ push_token: token, platform: 'web' });
      console.log('[push] Token registered successfully');
      return true;
    } else {
      console.warn('[push] getToken returned empty');
    }
  } catch (err) {
    console.error('[push] requestNotificationPermission failed:', err);
  }
  return false;
}

/**
 * Re-register the push token for returning users who already granted permission.
 * Does NOT prompt — only acts if permission is already 'granted'.
 */
export async function refreshPushToken(): Promise<boolean> {
  if (!isFirebaseConfigured()) {
    console.warn('[push] Firebase not configured — skipping token refresh');
    return false;
  }
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'granted') {
    console.log('[push] Permission not granted:', Notification.permission);
    return false;
  }

  try {
    const m = getFirebaseMessaging();
    if (!m) {
      console.warn('[push] Failed to get messaging instance');
      return false;
    }

    const swRegistration = await navigator.serviceWorker.getRegistration(
      '/firebase-cloud-messaging-push-scope',
    );
    console.log('[push] SW registration:', !!swRegistration, 'active:', !!swRegistration?.active);

    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) console.warn('[push] VAPID key missing');
    const token = await getToken(m, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      await authApi.updatePushToken({ push_token: token, platform: 'web' });
      console.log('[push] Token refreshed successfully');
      return true;
    } else {
      console.warn('[push] getToken returned empty');
    }
  } catch (err) {
    console.error('[push] refreshPushToken failed:', err);
  }
  return false;
}

/**
 * Listen for foreground push notifications.
 * Shows an in-app toast instead of a system notification.
 */
export function onForegroundMessage(
  callback: (payload: { title?: string; body?: string; data?: Record<string, string> }) => void,
): (() => void) | null {
  const m = getFirebaseMessaging();
  if (!m) return null;

  const unsubscribe = onMessage(m, (payload) => {
    callback({
      title: payload.data?.title || payload.notification?.title,
      body: payload.data?.body || payload.notification?.body,
      data: payload.data,
    });
  });

  return unsubscribe;
}

// ─── OAuth Sign-In ──────────────────────────────────────────────────────────

function getFirebaseAuth(): Auth {
  if (!isFirebaseConfigured()) {
    throw new Error('Firebase is not configured');
  }
  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  if (!auth) {
    auth = getAuth(app);
  }
  return auth;
}

/**
 * Detect environments where popups won't work (PWA standalone, iOS standalone).
 * In these contexts, signInWithPopup silently hangs rather than throwing,
 * so we must go straight to redirect.
 */
function mustUseRedirect(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if ((navigator as any).standalone) return true;
  return false;
}

/**
 * OAuth strategy:
 * - PWA standalone / iOS standalone → redirect (popups silently hang)
 * - All other contexts → popup (avoids third-party cookie issues with redirect)
 *
 * For redirect recovery, getRedirectResult() can return null when third-party
 * cookies are blocked. We fall back to onAuthStateChanged() which reads from
 * IndexedDB (first-party storage, always works).
 */
async function signInWithProvider(
  firebaseAuth: Auth,
  provider: GoogleAuthProvider | OAuthProvider,
  providerName: 'google' | 'apple',
): Promise<string | null> {
  if (mustUseRedirect()) {
    localStorage.setItem('nudge_oauth_pending', providerName);
    await signInWithRedirect(firebaseAuth, provider);
    return null; // page navigates away
  }

  try {
    const result = await signInWithPopup(firebaseAuth, provider);
    return await result.user.getIdToken();
  } catch (err: any) {
    // Popup blocked → fall back to redirect
    if (err?.code === 'auth/popup-blocked') {
      localStorage.setItem('nudge_oauth_pending', providerName);
      await signInWithRedirect(firebaseAuth, provider);
      return null;
    }
    // User closed popup — not an error, re-throw for caller to handle
    throw err;
  }
}

/**
 * Sign in with Google via Firebase Auth.
 * Returns the Firebase ID token, or null if redirect was initiated (page will reload).
 */
export async function signInWithGoogle(): Promise<string | null> {
  const firebaseAuth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');

  return signInWithProvider(firebaseAuth, provider, 'google');
}

/**
 * Sign in with Apple via Firebase Auth.
 */
export async function signInWithApple(): Promise<string | null> {
  const firebaseAuth = getFirebaseAuth();
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');

  return signInWithProvider(firebaseAuth, provider, 'apple');
}

/**
 * Check if we're returning from an OAuth redirect.
 * Call this on app load — returns the Firebase ID token if a redirect completed.
 *
 * Strategy: try getRedirectResult() first (works when third-party cookies are allowed).
 * If it returns null (cookies blocked), fall back to onAuthStateChanged() which reads
 * the persisted auth state from IndexedDB (first-party, always works).
 */
export async function checkOAuthRedirectResult(): Promise<{
  idToken: string;
  provider: 'google' | 'apple';
} | null> {
  const pending = localStorage.getItem('nudge_oauth_pending') as 'google' | 'apple' | null;
  if (!pending) return null;

  if (!isFirebaseConfigured()) {
    localStorage.removeItem('nudge_oauth_pending');
    return null;
  }

  const firebaseAuth = getFirebaseAuth();

  // Try getRedirectResult first (fast path when cookies work)
  try {
    const result = await getRedirectResult(firebaseAuth);
    if (result) {
      localStorage.removeItem('nudge_oauth_pending');
      const idToken = await result.user.getIdToken();
      console.log('[oauth] Redirect result resolved via getRedirectResult');
      return { idToken, provider: pending };
    }
  } catch (err: any) {
    // If it's an actual auth error (not just null result), bail
    if (err?.code && !err.code.includes('null-user')) {
      localStorage.removeItem('nudge_oauth_pending');
      throw err;
    }
  }

  // Fallback: wait for onAuthStateChanged (handles third-party cookie blocking).
  // Firebase persists the signed-in user to IndexedDB on the app's own origin,
  // so onAuthStateChanged will fire with the user even when getRedirectResult fails.
  console.log('[oauth] getRedirectResult returned null, waiting for onAuthStateChanged...');
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      localStorage.removeItem('nudge_oauth_pending');
      console.warn('[oauth] onAuthStateChanged timed out — no user found');
      resolve(null);
    }, 8000);

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
      clearTimeout(timeout);
      unsubscribe();
      localStorage.removeItem('nudge_oauth_pending');
      if (user) {
        try {
          const idToken = await user.getIdToken();
          console.log('[oauth] Redirect result resolved via onAuthStateChanged');
          resolve({ idToken, provider: pending! });
        } catch {
          resolve(null);
        }
      } else {
        console.warn('[oauth] onAuthStateChanged fired with no user');
        resolve(null);
      }
    });
  });
}

/**
 * Check if an OAuth redirect is pending (page is reloading after redirect).
 */
export function isOAuthRedirectPending(): boolean {
  return !!localStorage.getItem('nudge_oauth_pending');
}
